import { useCallback, useMemo, useState } from 'react';

import { itemHasUserCount, type ProductOrderItem } from '../data/product-order-item';

/**
 * The Reset Order confirmation for the items hook.
 *
 * `confirm()` answered synchronously and a modal does not, so the single
 * reset callback splits into "open the modal" and "on confirm, do the work".
 * Both halves read the touched set from the *current* `orderItems` rather
 * than capturing it when the modal opened: an `order_updated` push can change
 * it mid-dialog, and clearing a stale set would reset rows the user is no
 * longer looking at.
 *
 * `applyReset` — clearing the touched rows' overlay entries — stays with the
 * caller, which owns the overlay.
 */
export function useResetConfirm(
  orderItems: ProductOrderItem[],
  applyReset: (userItems: ProductOrderItem[]) => void
) {
  const [prompted, setPrompted] = useState(false);

  const userItems = useMemo(() => orderItems.filter(itemHasUserCount), [orderItems]);

  const promptResetOrder = useCallback(() => {
    if (userItems.length > 0) {
      setPrompted(true);
    }
  }, [userItems]);

  const dismissResetConfirm = useCallback(() => {
    setPrompted(false);
  }, []);

  const confirmResetOrder = useCallback(() => {
    setPrompted(false);
    applyReset(userItems);
  }, [applyReset, userItems]);

  return {
    // Never open on an empty set. The old `confirm()` returned before
    // prompting when nothing was touched, and a dialog reading "0 item(s)"
    // is worse than no dialog — this also covers the set emptying while the
    // modal is already open.
    showResetConfirm: prompted && userItems.length > 0,
    resetItemCount: userItems.length,
    promptResetOrder,
    dismissResetConfirm,
    confirmResetOrder,
  };
}
